import { useMutation, useQueryClient } from "@tanstack/react-query";
import { App, Checkbox, Form, Input, InputNumber, Modal, Select, Space, Table, Tag, Tooltip, Typography } from "antd";
import { api, unwrap, type Schemas } from "@/api/client";
import { useApplyFieldErrors, useErrorText } from "@/api/errors";
import { useAuth } from "@/auth/AuthProvider";
import { useI18n, type MessageKey } from "@/i18n";
import { useWarehouses } from "@/lib/useWarehouses";

export type Case = Schemas["AftersalesCase"];
export type CaseSummary = Schemas["AftersalesSummary"];
export type CaseKind = Schemas["AftersalesKind"];
export type CaseKindAny = Schemas["AftersalesKindAny"];
export type CaseStatus = Schemas["AftersalesStatus"];
export type Grade = Schemas["AftersalesGrade"];
export type Resolution = Schemas["AftersalesResolution"];
export type Reason = Schemas["AftersalesReason"];

export const KINDS: CaseKind[] = ["return_refund", "refund_only", "failed_delivery", "complaint", "exchange"];
export const REASONS: Exclude<Reason, "">[] = ["wrong_item", "damaged", "missing_item", "not_as_described", "defective", "changed_mind", "failed_delivery", "other"];
export const CHECKS = ["seal", "serial", "complete", "function"] as const;
export const GRADES: Grade[] = ["good", "damaged", "incomplete", "wrong_item"];
export const RESOLUTIONS: Resolution[] = ["refund", "partial_refund", "rejected", "appealed", "replacement", "no_action", "lost", "restocked"];

/** The goods come back to a warehouse. */
export const expectsGoods = (kind: CaseKindAny) => kind === "return_refund" || kind === "failed_delivery" || kind === "exchange";

const statusColor: Record<CaseStatus, string> = {
  open: "processing",
  receiving: "orange",
  received: "purple",
  resolved: "success",
  cancelled: "default",
};

export function CaseStatusTag({ status }: { status: CaseStatus }) {
  const { t } = useI18n();
  return <Tag color={statusColor[status]}>{t(`as.status.${status}`)}</Tag>;
}

const kindColor: Partial<Record<CaseKindAny, string>> = {
  return_refund: "blue",
  refund_only: "cyan",
  failed_delivery: "gold",
  complaint: "volcano",
  exchange: "geekblue",
  unidentified: "magenta",
};

export function CaseKindTag({ kind }: { kind: CaseKindAny }) {
  const { t } = useI18n();
  return <Tag color={kindColor[kind]}>{t(`as.kind.${kind}`)}</Tag>;
}

const gradeColor: Record<Grade, string> = { good: "success", damaged: "error", incomplete: "warning", wrong_item: "magenta" };

export function GradeTag({ grade }: { grade: Grade }) {
  const { t } = useI18n();
  return <Tag color={gradeColor[grade]}>{t(`as.gradeShort.${grade}`)}</Tag>;
}

export type DetailsValues = {
  kind: CaseKind;
  reason?: Reason;
  instructions?: string;
  checks?: string[];
  returnTrackingNo?: string;
  returnCarrier?: string;
  warehouseId?: string | null;
  quantities?: Record<string, number>;
};

/** What CS fills in on a case: kind, actual reason, what to check, where the goods go. */
export function DetailsFields({ kindLocked, warehouseLocked }: { kindLocked?: boolean; warehouseLocked?: boolean }) {
  const { t } = useI18n();
  const warehouses = useWarehouses();
  return (
    <>
      {!kindLocked && (
        <Form.Item name="kind" label={t("as.kindLabel")} rules={[{ required: true }]}>
          <Select options={KINDS.map((k) => ({ value: k, label: t(`as.kind.${k}`) }))} />
        </Form.Item>
      )}
      <Form.Item name="reason" label={t("as.reasonLabel")}>
        <Select allowClear placeholder={t("as.reason.none")} options={REASONS.map((r) => ({ value: r, label: t(`as.reason.${r}`) }))} />
      </Form.Item>
      <Form.Item name="instructions" label={t("as.instructions")} extra={t("as.instructionsHelp")}>
        <Input.TextArea rows={3} maxLength={2000} showCount />
      </Form.Item>
      <Form.Item name="checks" label={t("as.checks")}>
        <Checkbox.Group options={CHECKS.map((c) => ({ value: c, label: t(`as.check.${c}`) }))} />
      </Form.Item>
      <Form.Item name="returnTrackingNo" label={t("as.returnTracking")} extra={t("as.returnTrackingHelp")}>
        <Input maxLength={64} className="z-num" />
      </Form.Item>
      <Form.Item name="returnCarrier" label={t("as.returnCarrier")}>
        <Input maxLength={100} />
      </Form.Item>
      {!warehouseLocked && (
        <Form.Item name="warehouseId" label={t("as.warehouse")}>
          <Select allowClear options={warehouses.active.map((w) => ({ value: w.id, label: w.name }))} />
        </Form.Item>
      )}
    </>
  );
}

export type OrderForCase = {
  id: string;
  externalId: string;
  warehouseId: string | null;
  lines: { id: string; name: string; sellerSku: string; quantity: number; cancelled: boolean }[];
};

/** CS opens a case on an order: kind, items (quantities per line) and instructions. */
export function CreateCaseModal({ order, onClose, onCreated }: { order: OrderForCase | null; onClose: () => void; onCreated: (c: Case) => void }) {
  const { t } = useI18n();
  const { message } = App.useApp();
  const errorText = useErrorText();
  const queryClient = useQueryClient();
  const [form] = Form.useForm<DetailsValues>();
  const applyFieldErrors = useApplyFieldErrors();
  const kind = Form.useWatch("kind", form);
  const lines = (order?.lines ?? []).filter((l) => !l.cancelled && l.quantity > 0);
  const initial: DetailsValues = {
    kind: "return_refund",
    checks: [],
    warehouseId: order?.warehouseId ?? null,
    quantities: Object.fromEntries(lines.map((l) => [l.id, l.quantity])),
  };
  const create = useMutation({
    mutationFn: (v: DetailsValues) =>
      unwrap(
        api.POST("/v1/aftersales", {
          body: {
            orderId: order!.id,
            kind: v.kind,
            reason: v.reason ?? "",
            instructions: v.instructions ?? "",
            checks: (v.checks ?? []) as ("seal" | "serial" | "complete" | "function")[],
            returnTrackingNo: v.returnTrackingNo ?? "",
            returnCarrier: v.returnCarrier ?? "",
            warehouseId: v.warehouseId ?? null,
            lines: lines.map((l) => ({ orderLineId: l.id, quantity: v.quantities?.[l.id] ?? 0 })),
          },
        }),
      ),
    onSuccess: (c) => {
      void message.success(t("as.created", { number: c.number }));
      void queryClient.invalidateQueries({ queryKey: ["aftersales"] });
      onCreated(c);
    },
    onError: (err) => {
      if (!applyFieldErrors(form, err)) void message.error(errorText(err));
    },
  });
  return (
    <Modal
      open={!!order}
      title={t("as.create.title", { number: order?.externalId ?? "" })}
      onCancel={onClose}
      onOk={() => form.submit()}
      okText={t("as.add")}
      cancelText={t("common.cancel")}
      confirmLoading={create.isPending}
      width={680}
      destroyOnHidden
      afterClose={() => form.resetFields()}
    >
      <Form form={form} layout="vertical" initialValues={initial} onFinish={(v) => create.mutate(v)} requiredMark={false}>
        <DetailsFields />
        <Typography.Text strong>{t("as.create.lines")}</Typography.Text>
        <Typography.Paragraph type="secondary" style={{ fontSize: 12, marginBottom: 8 }}>
          {expectsGoods(kind ?? "return_refund") ? t("as.create.linesHelp") : t("as.create.linesClaimHelp")}
        </Typography.Paragraph>
        <Table
          size="small"
          rowKey="id"
          pagination={false}
          dataSource={lines}
          columns={[
            {
              title: t("as.item"),
              render: (_, l) => (
                <span>
                  <Typography.Text className="z-num">{l.sellerSku}</Typography.Text>
                  <Typography.Text type="secondary"> · {l.name}</Typography.Text>
                </span>
              ),
            },
            {
              title: t("as.create.qty"),
              width: 150,
              render: (_, l) => (
                <Form.Item name={["quantities", l.id]} noStyle>
                  <InputNumber min={0} max={l.quantity} precision={0} suffix={`/ ${l.quantity}`} style={{ width: 130 }} />
                </Form.Item>
              ),
            },
          ]}
        />
      </Form>
    </Modal>
  );
}

function msUntil(iso: string): number {
  return new Date(iso).getTime() - Date.now();
}

/**
 * The marketplace's deadline for the seller's answer: overdue or due within
 * 12 hours in red, within a day in orange, otherwise the time it is due.
 */
export function RespondDeadline({ at }: { at: string | null | undefined }) {
  const { t, formatDateTime } = useI18n();
  const { me } = useAuth();
  if (!at) return null;
  const when = formatDateTime(at, me?.workspace?.timezone);
  const left = msUntil(at);
  const hours = Math.max(1, Math.floor(left / 3600_000));
  const [color, label] =
    left < 0
      ? ["error", t("as.respond.overdue")]
      : left < 12 * 3600_000
        ? ["error", t("as.respond.inHours", { n: hours })]
        : left < 24 * 3600_000
          ? ["warning", t("as.respond.inHours", { n: hours })]
          : ["processing", t("as.respond.until", { at: when })];
  return (
    <Tooltip title={t("as.respond.deadline", { at: when })}>
      <Tag color={color}>{label}</Tag>
    </Tooltip>
  );
}

type CaseGlance = {
  kind: CaseKindAny;
  status: CaseStatus;
  platformStatus: string;
  resolution: Resolution | null;
  respondAction: Schemas["AftersalesRespondAction"];
  respondBy: string | null;
};

/** Where a case stands, in a word: what the order list and order page show beside the order's status. */
export function caseProgress(c: CaseGlance): { label: MessageKey; color: string } {
  if (c.respondAction === "request") return { label: "as.progress.respond", color: "error" };
  if (c.respondAction === "receipt") return { label: "as.progress.receipt", color: "error" };
  switch (c.status) {
    case "receiving":
      return { label: "as.progress.receiving", color: "orange" };
    case "received":
      return { label: "as.progress.received", color: "purple" };
    case "resolved":
      return { label: c.resolution ? `as.resolution.${c.resolution}` : "as.progress.resolved", color: "success" };
    case "cancelled":
      return { label: "as.progress.cancelled", color: "default" };
  }
  if (!expectsGoods(c.kind)) return { label: "as.progress.decide", color: "processing" };
  if (c.platformStatus === "AWAITING_BUYER_SHIP") return { label: "as.progress.awaitingShip", color: "processing" };
  if (c.platformStatus === "BUYER_SHIPPED_ITEM") return { label: "as.progress.inTransit", color: "processing" };
  return { label: "as.progress.awaitingGoods", color: "processing" };
}

/** "Retur: perlu respons", "Refund: ditolak", ... with the answer's deadline when one is due. */
export function CaseProgressTag({ c, onClick }: { c: CaseGlance; onClick?: () => void }) {
  const { t } = useI18n();
  const p = caseProgress(c);
  const stage = t(p.label);
  return (
    <Space size={4} wrap onClick={onClick} style={onClick ? { cursor: "pointer" } : undefined}>
      <Tag color={p.color} style={{ marginInlineEnd: 0 }}>
        {t(`as.kindShort.${c.kind}`)}: {stage}
      </Tag>
      {c.respondAction && <RespondDeadline at={c.respondBy} />}
    </Space>
  );
}
